import reduceReducers from 'reduce-reducers';
import { DeliveryLogType } from '@dev/ee-api/ee-api-models';
import { Reducer } from 'redux';

import { DEFAULT_PER_PAGE_COUNT } from '../../services/constants';
import { CampaignsActionTypes } from '../campaigns/types';
import { deviceDetails } from './deviceDetails/reducer';
import { ReportsActionTypes, ReportsState } from './types';

const defaultState: ReportsState = {
    limit: DEFAULT_PER_PAGE_COUNT,
    offset: 0,
    log: null,
    logsLoading: false,
    resendLoading: false,
    eventLog: null,
    eventLogLoading: false,
    eventLogParams: {
        offset: 0,
        limit: DEFAULT_PER_PAGE_COUNT,
        statuses: [DeliveryLogType.ReadyToSend, DeliveryLogType.WaitingToRetry, DeliveryLogType.Sending],
        messageCategory: null,
        email: null,
    },
    eventLogByChannelNameLoading: false,
    eventLogParamsByChannelName: {
        channelName: null,
        offset: 0,
        limit: DEFAULT_PER_PAGE_COUNT,
        statuses: [DeliveryLogType.All],
        messageCategory: null,
        email: null,
    },
    eventLogByChannelName: null,
    eventsByChannelNameCount: null,
    resultsByChannelNameCount: null,
    eventsCount: null,
    resultsCount: null,
    linkTrackingLoading: false,
    isLoadingLinks: false,
    linkTrackingAbSplit: {
        loading: false,
        collection: null,
        totalCount: null,
        offset: 0,
        limit: DEFAULT_PER_PAGE_COUNT,
    },
    statuses: [DeliveryLogType.All],
    reportsSummaryLoading: false,
    reportsSummary: null,
    linkTrackingActivePage: 1,
    linkTrackingPerPage: 15,
    linkTrackingList: [],
    linkContactsPagination: {
        activePage: 1,
        perPage: 15,
    },
    linkContactsLoading: false,
    linkContactsAllLoading: false,
    locationStats: { dataSet: [], loading: false, totalCount: 0 },
    deviceDetails: {
        deviceDetailsStats: [],
        loading: false,
    },
};

const reportsStore: Reducer<ReportsState> = (state: ReportsState = defaultState, action): ReportsState => {
    switch (action.type) {
        case ReportsActionTypes.EVENT_LOG_START:
            return { ...state, eventLogLoading: action.payload.eventLogLoading };
        case ReportsActionTypes.EVENT_LOG_SUCCESS:
            return {
                ...state,
                eventLog: action.payload.eventLog,
                eventsCount: action.payload.eventsCount,
                resultsCount: action.payload.resultsCount,
                eventLogParams: action.payload.eventLogParams,
                eventLogLoading: action.payload.eventLogLoading,
            };
        case ReportsActionTypes.EVENT_LOG_BY_CHANNELNAME_ERROR:
            return { ...state, eventLogByChannelNameLoading: action.payload.eventLogByChannelNameLoading };
        case ReportsActionTypes.EVENT_LOG_BY_CHANNELNAME_START:
            return { ...state, eventLogByChannelNameLoading: action.payload.eventLogByChannelNameLoading };
        case ReportsActionTypes.EVENT_LOG_BY_CHANNELNAME_SUCCESS:
            return {
                ...state,
                eventLogByChannelName: action.payload.eventLogByChannelName,
                eventsByChannelNameCount: action.payload.eventsByChannelNameCount,
                resultsByChannelNameCount: action.payload.resultsByChannelNameCount,
                eventLogParamsByChannelName: action.payload.eventLogParamsByChannelName,
                eventLogByChannelNameLoading: action.payload.eventLogByChannelNameLoading,
            };
        case ReportsActionTypes.EVENT_LOG_ERROR:
            return { ...state, eventLogLoading: action.payload.eventLogLoading };
        case ReportsActionTypes.FETCH_START:
            return { ...state, statuses: action.payload.statuses, logsLoading: !action.payload?.params?.infinityScroll };
        case ReportsActionTypes.LOAD_LINK_TRACKING_ABSPLIT_START:
            return { ...state, linkTrackingAbSplit: { loading: true } };
        case ReportsActionTypes.LOAD_LINK_TRACKING_ABSPLIT_SUCCESS:
            return {
                ...state,
                linkTrackingAbSplit: {
                    loading: action.payload.loading,
                    collection: action.payload.collection,
                    totalCount: action.payload.totalCount,
                    offset: action.payload.offset,
                    limit: action.payload.limit,
                },
            };
        case ReportsActionTypes.LOAD_LINK_TRACKING_ABSPLIT_ERROR:
            return { ...state, linkTrackingAbSplit: { loading: false } };
        case ReportsActionTypes.FETCH_ERROR:
        case ReportsActionTypes.FETCH_SUCCESS:
        case ReportsActionTypes.FETCH_CONTACT_LINKS_START:
        case ReportsActionTypes.FETCH_CONTACT_LINKS_ERROR:
        case ReportsActionTypes.FETCH_CONTACT_LINKS_SUCCESS:
            return { ...state, ...action.payload };
        case ReportsActionTypes.LOAD_LINK_TRACKING_SUCCESS:
            return {
                ...state,
                linkTrackingList: action.payload.linkTrackingList,
                linkTrackingLoading: false,
            };
        case ReportsActionTypes.LOAD_LINK_TRACKING_ERROR:
        case ReportsActionTypes.GET_REPORTS_SUMMARY_START:
        case ReportsActionTypes.GET_REPORTS_SUMMARY_SUCCESS:
        case ReportsActionTypes.GET_REPORTS_SUMMARY_ERROR:
        case ReportsActionTypes.LINK_CONTACTS_START:
        case ReportsActionTypes.LINK_CONTACTS_SUCCESS:
        case ReportsActionTypes.LINK_CONTACTS_ERROR:
            return {
                ...state,
                ...action.payload,
            };
        case ReportsActionTypes.LINK_CONTACTS_ALL_START:
            return {
                ...state,
                linkContactsAllLoading: true,
            };
        case ReportsActionTypes.LINK_CONTACTS_ALL_ERROR:
            return {
                ...state,
                linkContactsAllLoading: false,
            };
        case ReportsActionTypes.LINK_CONTACTS_ALL_SUCCESS:
            return {
                ...state,
                linkContactsAllLoading: false,
                linkContactsAllList: action.payload.linkContactsAllList,
            };
        case CampaignsActionTypes.GET_CAMPAIGN_REPORTS_SUMMARY:
            return {
                ...state,
                reportsSummaryLoading: true,
            };
        case ReportsActionTypes.LOAD_LINK_TRACKING_START:
            return {
                ...state,
                linkTrackingLoading: true,
            };
        case CampaignsActionTypes.CLEAR_CAMPAIGN_REPORTS_SUMMARY:
            return { ...state, reportsSummary: null };
        case ReportsActionTypes.CLEAR_CAMPAIGN_LINK_TRACKING:
            return { ...state, linkTrackingList: [] };
        case ReportsActionTypes.CLEAR_CAMPAIGN_REPORTS_DATA:
            return {
                ...state,
                eventLogParamsByChannelName: {
                    ...state.eventLogParamsByChannelName,
                    email: null,
                    offset: 0,
                    limit: 25,
                    statuses: [DeliveryLogType.All],
                    messageCategory: null,
                },
                linkTrackingList: [],
            };
        case ReportsActionTypes.GET_REPORTS_LOCATION_STATS_BY_NAME_START:
            return {
                ...state,
                locationStats: { loading: true },
            };
        case ReportsActionTypes.GET_REPORTS_LOCATION_STATS_BY_NAME_SUCCESS:
            return {
                ...state,
                locationStats: {
                    dataSet: action.payload.locationStatsData.data,
                    loading: false,
                    totalCount: action.payload.locationStatsData.count,
                },
            };
        case ReportsActionTypes.CLEAR_LOG:
            return { ...state, log: null };
        case ReportsActionTypes.RESEND_START:
            return { ...state, resendLoading: true };
        case ReportsActionTypes.RESEND_SUCCESS:
            return { ...state, resendLoading: false };
        case ReportsActionTypes.RESEND_ERROR:
            return { ...state, resendLoading: false };
        case ReportsActionTypes.UPDATE_CHANNEL_EVENT_COUNTS:
            return {
                ...state,
                eventsCounts: action.payload.eventsCounts,
                countsLoading: false,
            };
        case ReportsActionTypes.FETCH_CHANNEL_EVENTS_COUNTS:
            return {
                ...state,
                countsLoading: true,
            };
        default:
            return state;
    }
};

const reducer = reduceReducers(reportsStore, deviceDetails);

export { reducer as reports };

export default reducer;
